import React, { useEffect, useState } from "react";
import API from "../../../services/api";

const Voters = () => {
  const [voters, setVoters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [gender, setGender] = useState("");
  const [boothNo, setBoothNo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // ── Fetch Voters ──────────────────────────────────────────
  const fetchVoters = async (page = 1) => {
    setLoading(true);
    try {
      const res = await API.get("/voters/", {
        params: {
          page,
          per_page: 10,
          search: search || undefined,
          gender: gender || undefined,
          booth_no: boothNo || undefined,
        },
      });
      setVoters(res.data.data.voters);
      setTotalPages(res.data.data.pagination.total_pages);
      setTotalRecords(res.data.data.pagination.total);
      setCurrentPage(page);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVoters();
  }, []);

  // ── Upload Excel ──────────────────────────────────────────
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) return alert("Please select a file");
    setUploading(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      const res = await API.post("/voters/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult({
        type: "success",
        inserted: res.data.data.inserted,
        skipped: res.data.data.skipped,
        message: res.data.message,
      });
      setSelectedFile(null);
      fetchVoters(1);
    } catch (err) {
      setUploadResult({
        type: "error",
        message: err.response?.data?.message || "Upload failed",
      });
    } finally {
      setUploading(false);
    }
  };

  // ── Search ────────────────────────────────────────────────
  const handleSearch = (e) => {
    e.preventDefault();
    fetchVoters(1);
  };

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h4 className="fw-bold mb-1">Voters</h4>
          <p className="text-muted small mb-0">
            Total <strong>{totalRecords.toLocaleString()}</strong> voters
            registered
          </p>
        </div>
        <button
          className="btn text-white fw-semibold px-4"
          style={{ background: "linear-gradient(135deg, #1a3c5e, #2d6a4f)" }}
          onClick={() => {
            setShowUpload(!showUpload);
            setUploadResult(null);
          }}
        >
          <i className="bi bi-file-earmark-excel me-2"></i>
          Upload Excel
        </button>
      </div>

      {/* Upload Panel */}
      {showUpload && (
        <div className="card border-0 shadow-sm rounded-4 mb-3">
          <div className="card-body p-4">
            <h6 className="fw-semibold mb-3">
              <i className="bi bi-upload me-2 text-success"></i>
              Upload Voter Excel File
            </h6>

            {uploadResult && (
              <div
                className={`alert ${uploadResult.type === "success" ? "alert-success" : "alert-danger"} py-2 small mb-3`}
              >
                {uploadResult.type === "success" ? (
                  <>
                    <i className="bi bi-check-circle me-2"></i>
                    <strong>{uploadResult.message}</strong>
                    <div className="mt-1">
                      ✅ Inserted: <strong>{uploadResult.inserted}</strong>{" "}
                      &nbsp; ⏭️ Skipped: <strong>{uploadResult.skipped}</strong>
                    </div>
                  </>
                ) : (
                  <>
                    <i className="bi bi-exclamation-circle me-2"></i>
                    {uploadResult.message}
                  </>
                )}
              </div>
            )}

            <form onSubmit={handleUpload}>
              <div className="row g-3 align-items-end">
                <div className="col-12 col-md-8">
                  <label className="form-label small fw-medium">
                    Select Excel File (.xlsx, .xls)
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    accept=".xlsx,.xls"
                    onChange={(e) => setSelectedFile(e.target.files[0])}
                  />
                  <div className="form-text">
                    Excel columns: Booth No, Section No, Serial No, First Name,
                    Last Name, EPIC No, DOB, Gender, Age, Mobile, Address, PS
                    Name
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <button
                    type="submit"
                    className="btn w-100 text-white fw-semibold"
                    style={{
                      background: "linear-gradient(135deg, #1a3c5e, #2d6a4f)",
                    }}
                    disabled={uploading || !selectedFile}
                  >
                    {uploading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-cloud-upload me-2"></i>Upload Now
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card border-0 shadow-sm rounded-4 mb-3">
        <div className="card-body p-3">
          <form onSubmit={handleSearch}>
            <div className="row g-2 align-items-end">
              <div className="col-12 col-md-5">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name, EPIC no, mobile..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="col-6 col-md-2">
                <select
                  className="form-select"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                >
                  <option value="">All Gender</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                  <option value="O">Other</option>
                </select>
              </div>
              <div className="col-6 col-md-2">
                <input
                  type="number"
                  className="form-control"
                  placeholder="Booth No"
                  value={boothNo}
                  onChange={(e) => setBoothNo(e.target.value)}
                />
              </div>
              <div className="col-6 col-md-2">
                <button type="submit" className="btn btn-primary w-100">
                  <i className="bi bi-search me-1"></i> Search
                </button>
              </div>
              <div className="col-6 col-md-1">
                <button
                  type="button"
                  className="btn btn-outline-secondary w-100"
                  onClick={() => {
                    setSearch("");
                    setGender("");
                    setBoothNo("");
                    fetchVoters(1);
                  }}
                >
                  Reset
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Table */}
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
            </div>
          ) : voters.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-people fs-1 d-block mb-2"></i>
              No voters found. Upload an Excel file to get started.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="px-4 py-3 small text-muted fw-semibold">
                      #
                    </th>
                    <th className="py-3 small text-muted fw-semibold">
                      EPIC No
                    </th>
                    <th className="py-3 small text-muted fw-semibold">Name</th>
                    <th className="py-3 small text-muted fw-semibold">
                      Gender
                    </th>
                    <th className="py-3 small text-muted fw-semibold">Age</th>
                    <th className="py-3 small text-muted fw-semibold">Booth</th>
                    <th className="py-3 small text-muted fw-semibold">
                      Mobile
                    </th>
                    <th className="py-3 small text-muted fw-semibold">
                      Address
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {voters.map((v, idx) => (
                    <tr key={v.voter_id}>
                      <td className="px-4 text-muted small">
                        {(currentPage - 1) * 10 + idx + 1}
                      </td>
                      <td>
                        <span className="badge bg-primary-subtle text-primary small">
                          {v.epic_no}
                        </span>
                      </td>

                      <td>
                        <div className="fw-semibold small">
                          {v.firstname_en} {v.lastname_en}
                        </div>
                        {v.firstname_v1 && (
                          <div
                            className="text-muted"
                            style={{ fontSize: "11px" }}
                          >
                            {v.firstname_v1} {v.lastname_v1}
                          </div>
                        )}
                      </td>
                      <td>
                        {v.gender === "M" ? (
                          <span className="badge bg-info-subtle text-info">
                            Male
                          </span>
                        ) : v.gender === "F" ? (
                          <span className="badge bg-pink-subtle text-danger">
                            Female
                          </span>
                        ) : (
                          <span className="badge bg-secondary-subtle text-secondary">
                            {v.gender}
                          </span>
                        )}
                      </td>
                      <td className="small">{v.age || "—"}</td>
                      <td>
                        <span className="badge bg-light text-dark border small">
                          Booth {v.booth_no}
                        </span>
                      </td>
                      <td className="small text-muted">{v.mobile || "—"}</td>
                      <td
                        className="small text-muted"
                        style={{
                          maxWidth: "200px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {v.address_en || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="card-footer bg-white border-0 d-flex justify-content-between align-items-center py-3 px-4">
            <span className="text-muted small">
              Page {currentPage} of {totalPages}
            </span>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => fetchVoters(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                  const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                  if (page > totalPages) return null;
                  return (
                    <li
                      key={page}
                      className={`page-item ${currentPage === page ? "active" : ""}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => fetchVoters(page)}
                      >
                        {page}
                      </button>
                    </li>
                  );
                })}
                <li
                  className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => fetchVoters(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
};

export default Voters;
